import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { SvgIconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import {
  getDefectiveLevelColor,
  getDefectiveLevelFrom,
} from '@euler/functions/getDefectiveLevel';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  MaintenancePlan,
  MaintenancePlanDetail,
} from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { formatDate, formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { formatVehicleBrandModel } from '@euler/utils/vehicle/brandModel';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FC, memo, useCallback, useMemo } from 'react';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useTheme } from 'styled-components';

type ActionItemType =
  | 'cancel-plan-detail'
  | 'enable-alert'
  | 'disable-alert'
  | 'enable-all-alert'
  | 'disable-all-alert'
  | 'enable-customer-alert'
  | 'disable-customer-alert'
  | 'edit'
  | 'cancel';

type ActionItem = {
  type: ActionItemType;
  label: string;
};

const kActionItemConfig: {
  [p in ActionItemType]: {
    label: string;
  };
} = {
  'cancel-plan-detail': { label: '取消保养计划' },
  'enable-all-alert': { label: '开启所有提醒' },
  'disable-all-alert': { label: '关闭所有提醒' },
  'enable-alert': { label: '开启门店提醒' },
  'disable-alert': { label: '关闭门店提醒' },
  'enable-customer-alert': { label: '开启客户提醒' },
  'disable-customer-alert': { label: '关闭客户提醒' },
  edit: { label: '修改保养计划' },
  cancel: { label: '返回' },
};

export const MaintenancePlanCell = memo(
  ({
    plan,
    editable,
    style,
    onEdit,
    onViewServiceRecord,
    onPlanDetailUpdated,
    onPlanDetailRemoved,
  }: {
    plan: MaintenancePlan;
    editable: boolean;
    onEdit?: (plan: MaintenancePlan, detail: MaintenancePlanDetail) => void;
    onViewServiceRecord?: (plan: MaintenancePlan) => void;
    onPlanDetailUpdated?: (
      plan: MaintenancePlan,
      detail: MaintenancePlanDetail,
    ) => void;
    onPlanDetailRemoved?: (
      plan: MaintenancePlan,
      detail: MaintenancePlanDetail,
    ) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();

    const onViewServiceRecordPress = useCallback(() => {
      onViewServiceRecord?.(plan);
    }, [onViewServiceRecord, plan]);

    const onDetailUpdated = useCallback(
      (detail: MaintenancePlanDetail) => {
        onPlanDetailUpdated?.(plan, detail);
      },
      [onPlanDetailUpdated, plan],
    );

    const onDetailRemoved = useCallback(
      (detail: MaintenancePlanDetail) => {
        onPlanDetailRemoved?.(plan, detail);
      },
      [onPlanDetailRemoved, plan],
    );

    return (
      <StackLayout
        style={style}
        css={`
          padding-top: ${rpx(10)}px;
          padding-bottom: ${rpx(10)}px;
          padding-left: ${rpx(15)}px;
          padding-right: ${rpx(15)}px;
          background-color: #fff;
          margin-top: ${rpx(0)}px;
          margin-bottom: ${rpx(10)}px;
        `}
      >
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <StackLayout
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={rpx(5)}
          >
            <Typography.Text bold size={rpx(20)}>
              {plan.licensePlateNo}
            </Typography.Text>
            <Typography.Text thin size={rpx(16)} color="#888">
              {formatVehicleBrandModel(
                plan.vehicleBrandName,
                plan.vehicleModel,
                '•',
              )}
            </Typography.Text>
          </StackLayout>
          <LinkButton
            text={plan.serviceRecordId ? '服务记录' : '服务中'}
            style={{ alignItems: 'center' }}
            textStyle={{
              fontSize: rpx(12),
              lineHeight: rpx(20),
              color: plan.serviceRecordId ? theme.link : StatusColors.Warn,
            }}
            hitSlop={PredefinedHitSlops.large}
            onPress={onViewServiceRecordPress}
          >
            <Icon
              name="chevron-thin-right"
              size={rpx(10)}
              color={plan.serviceRecordId ? theme.link : StatusColors.Warn}
            />
          </LinkButton>
        </StackLayout>
        <StackLayout
          direction="row-reverse"
          justifyContent="space-between"
          alignItems="center"
          style={{
            marginTop: rpx(5),
            marginBottom: rpx(10),
            borderWidth: 1,
            borderColor: '#e2e2e2',
            borderRadius: rpx(3),
            backgroundColor: '#eee',
          }}
          divider={
            <View
              style={{ width: 1, backgroundColor: '#e2e2e2', height: '100%' }}
            />
          }
        >
          <Typography.Text
            letterSpacing={rpx(-0.5)}
            size={rpx(12)}
            lineHeight={rpx(24)}
            color="#777"
            align="center"
            flex={2}
            numberOfLines={1}
            ellipsizeMode="tail"
            medium
          >
            服务顾问: {plan.sourceSaName}
          </Typography.Text>
          <Typography.Text
            letterSpacing={rpx(-0.5)}
            size={rpx(12)}
            lineHeight={rpx(24)}
            color="#777"
            align="center"
            flex={2}
            numberOfLines={1}
            ellipsizeMode="tail"
            medium
          >
            进店里程: {plan.currentMileage}KM
          </Typography.Text>
          <Typography.Text
            letterSpacing={rpx(-0.5)}
            size={rpx(12)}
            lineHeight={rpx(24)}
            color="#777"
            align="center"
            flex={3}
            numberOfLines={1}
            ellipsizeMode="tail"
            medium
          >
            进店时间: {formatDateTime(plan.orderedAt!)}
          </Typography.Text>
        </StackLayout>
        <StackLayout gap={rpx(1)} alignItems="stretch">
          {plan.details.map(detail => (
            <MaintenancePlanDetailCell
              editable={editable}
              plan={plan}
              detail={detail}
              key={detail.id}
              onEdit={onEdit}
              onUpdated={onDetailUpdated}
              onRemoved={onDetailRemoved}
            />
          ))}
        </StackLayout>
      </StackLayout>
    );
  },
);

export const MaintenancePlanDetailCell = memo(
  ({
    plan,
    editable,
    detail,
    style,
    nameStyle,
    nameContainerStyle,
    onEdit,
    onUpdated,
    onRemoved,
  }: {
    plan: MaintenancePlan;
    editable: boolean;
    detail: MaintenancePlanDetail;
    nameContainerStyle?: StyleProp<ViewStyle>;
    nameStyle?: StyleProp<TextStyle>;
    style?: StyleProp<ViewStyle>;
    onEdit?: (plan: MaintenancePlan, detail: MaintenancePlanDetail) => void;
    onUpdated?: (detail: MaintenancePlanDetail) => void;
    onRemoved?: (detail: MaintenancePlanDetail) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const confirm = useConfirm();
    const defectiveLevel = getDefectiveLevelFrom(detail);
    const color = getDefectiveLevelColor(theme, defectiveLevel);

    const { maintenancePlanService } = useServiceFactory();
    const { showActionSheetWithOptions } = useActionSheet();

    const actionItems = useMemo<ActionItem[]>(() => {
      const itemTypes: ActionItemType[] = [];
      itemTypes.push('edit');
      if (detail.isAlertEnabled || detail.isAlertEnabledForCustomer) {
        itemTypes.push('disable-all-alert');
      } else if (!detail.isAlertEnabled || !detail.isAlertEnabledForCustomer) {
        itemTypes.push('enable-all-alert');
      }
      itemTypes.push(detail.isAlertEnabled ? 'disable-alert' : 'enable-alert');
      itemTypes.push(
        detail.isAlertEnabledForCustomer
          ? 'disable-customer-alert'
          : 'enable-customer-alert',
      );
      itemTypes.push('cancel-plan-detail');
      itemTypes.push('cancel');
      return itemTypes.map(type => ({
        type,
        label: kActionItemConfig[type].label,
      }));
    }, [detail.isAlertEnabled, detail.isAlertEnabledForCustomer]);

    const onCancelPlanDetail = usePersistFn(async () => {
      if (
        !(await confirm({
          title: '确认',
          message: '您即将删除该保养计划，该操作不可撤销，是否继续?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.removeMaintenancePlanDetail(
        plan.planNo,
        detail.id,
      );
      onRemoved?.(detail);
    });

    const onToggleAllAlert = usePersistFn(async (enabled: boolean) => {
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的所有提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        plan.planNo,
        detail.id,
        {
          enabled,
          enabledForCustomer: enabled,
        },
      );
      onUpdated?.({
        ...detail,
        isAlertEnabled: enabled,
        isAlertEnabledForCustomer: enabled,
      });
    });

    const onToggleAlert = usePersistFn(async (enabled: boolean) => {
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的门店提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        plan.planNo,
        detail.id,
        {
          enabled,
        },
      );
      onUpdated?.({
        ...detail,
        isAlertEnabled: enabled,
      });
    });

    const onToggleAlertForCustomer = usePersistFn(async (enabled: boolean) => {
      if (
        !enabled &&
        !(await confirm({
          title: '确认',
          message: '是否确认关闭该保养计划的客户提醒?',
        }))
      ) {
        return;
      }
      await maintenancePlanService.updateMaintenancePlanDetailAlertSettings(
        plan.planNo,
        detail.id,
        {
          enabledForCustomer: enabled,
        },
      );
      onUpdated?.({
        ...detail,
        isAlertEnabledForCustomer: enabled,
      });
    });

    const onPress = useCallback(async () => {
      if (!editable) return;
      showActionSheetWithOptions(
        {
          options: actionItems.map(x => x.label),
          cancelButtonIndex: actionItems.findIndex(x => x.type === 'cancel'),
          destructiveButtonIndex: actionItems.findIndex(
            x => x.type === 'cancel-plan-detail',
          ),
        },
        buttonIndex => {
          if (buttonIndex == null || buttonIndex < 0) return;
          const actionItem = actionItems[buttonIndex];
          switch (actionItem.type) {
            case 'edit':
              onEdit?.(plan, detail);
              break;
            case 'cancel-plan-detail':
              void onCancelPlanDetail();
              break;
            case 'enable-all-alert':
            case 'disable-all-alert':
              void onToggleAllAlert(actionItem.type === 'enable-all-alert');
              break;
            case 'enable-alert':
            case 'disable-alert':
              void onToggleAlert(actionItem.type === 'enable-alert');
              break;
            case 'enable-customer-alert':
            case 'disable-customer-alert':
              void onToggleAlertForCustomer(
                actionItem.type === 'enable-customer-alert',
              );
              break;
            default:
              break;
          }
        },
      );
    }, [
      actionItems,
      detail,
      editable,
      onCancelPlanDetail,
      onEdit,
      onToggleAlert,
      onToggleAlertForCustomer,
      onToggleAllAlert,
      plan,
      showActionSheetWithOptions,
    ]);

    return (
      <TouchableOpacity style={style} onPress={onPress} disabled={!editable}>
        <StackLayout style={style} direction="row" alignItems="stretch" gap={0}>
          <Center
            css={`
              width: 25%;
              flex-shrink: 0;
              flex-grow: 0;
              background-color: #06a7c4;
              padding: ${rpx(5)}px ${rpx(10)}px;
            `}
            style={nameContainerStyle}
          >
            <Typography.Text
              color="white"
              bold
              align="center"
              size={rpx(15)}
              style={nameStyle}
            >
              {detail.name}
            </Typography.Text>
          </Center>
          <StackLayout
            direction="row"
            justifyContent="space-between"
            css={`
              background-color: #f9f9f9;
              padding: ${rpx(5)}px ${rpx(10)}px;
              flex: 1;
            `}
          >
            <StackLayout gap={rpx(3)} style={{ flex: 1 }}>
              <Typography.Text size={rpx(12)} color={'#222'}>
                <Text style={{ opacity: 0.5 }}>下次保养里程: </Text>
                {detail.nextServiceMileage
                  ? `${detail.nextServiceMileage}KM`
                  : '-'}
              </Typography.Text>
              <Typography.Text size={rpx(12)} color={'#222'}>
                <Text style={{ opacity: 0.5 }}>下次保养日期: </Text>
                {formatDate(detail.nextServiceDate)}
              </Typography.Text>
              <Typography.Text size={rpx(12)} color={'#222'}>
                <Text style={{ opacity: 0.5 }}>计划创建来自: </Text>
                {detail.createdByName}
              </Typography.Text>
              <Typography.Text size={rpx(12)} color={'#222'}>
                <Text style={{ opacity: 0.5 }}>计划创建时间: </Text>
                {formatDateTime(detail.createdAt)}
              </Typography.Text>
              {detail.remark?.trim() ? (
                <Typography.Text size={rpx(12)} color={'#222'}>
                  <Text style={{ opacity: 0.5 }}>保养计划备注: </Text>
                  {detail.remark}
                </Typography.Text>
              ) : null}
            </StackLayout>
            {detail.isAlertEnabled && detail.isAlertEnabledForCustomer ? (
              <AlertStatusInfo enabled={true} iconSize={rpx(26)} />
            ) : !detail.isAlertEnabled && !detail.isAlertEnabledForCustomer ? (
              <AlertStatusInfo enabled={false} iconSize={rpx(26)} />
            ) : (
              <StackLayout
                spacing={rpx(4)}
                justifyContent="center"
                alignItems="center"
              >
                <AlertStatusInfo enabled={detail.isAlertEnabled} label="门店" />
                <AlertStatusInfo
                  enabled={detail.isAlertEnabledForCustomer}
                  label="客户"
                />
              </StackLayout>
            )}
          </StackLayout>
          <View
            css={`
              flex-shrink: 0;
              width: ${rpx(4)}px;
              background-color: ${color};
            `}
          />
        </StackLayout>
      </TouchableOpacity>
    );
  },
);

export const AlertStatusInfo = memo(
  ({
    enabled,
    label,
    iconSize,
    style,
  }: {
    enabled: boolean;
    label?: string;
    iconSize?: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    iconSize = iconSize ?? rpx(12);
    const iconColor = enabled ? StatusColors.Success : '#ccc';
    const IconComponent = enabled ? AlertIcon : NoAlertIcon;
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        style={style}
        spacing={rpx(3)}
      >
        <IconComponent fill={iconColor} size={iconSize} />
        {label ? (
          <Typography.Text
            color={iconColor}
            size={iconSize}
            lineHeight={iconSize * 1.4}
          >
            {label}
          </Typography.Text>
        ) : null}
      </StackLayout>
    );
  },
);

const NoAlertIcon: FC<SvgIconProps> = memo(props => {
  const { fill = '#ccc', ...svgProps } = useSvgProps(props, 24, 24);
  return (
    <Svg viewBox="0 0 1024 1024" {...svgProps}>
      <Path
        d="M802.133333 642.844444V426.666667c0-45.511111-11.377778-85.333333-28.444444-125.155556l125.155555-125.155555c5.688889-5.688889 11.377778-17.066667 5.688889-28.444445s-11.377778-17.066667-22.755555-22.755555-22.755556 0-28.444445 11.377777l-113.777777 113.777778c-34.133333-45.511111-85.333333-79.644444-147.911112-96.711111-11.377778-28.444444-45.511111-51.2-79.644444-51.2-34.133333 0-62.577778 17.066667-79.644444 51.2-119.466667 34.133333-210.488889 147.911111-210.488889 273.066667v216.177777c-68.266667 34.133333-108.088889 79.644444-108.088889 130.844445 0 11.377778 5.688889 28.444444 28.444444 45.511111 5.688889 5.688889 11.377778 5.688889 22.755556 11.377778l-34.133334 34.133333c-5.688889 5.688889-11.377778 17.066667-11.377777 28.444445 5.688889 11.377778 11.377778 17.066667 22.755555 22.755555 11.377778 5.688889 22.755556 0 28.444445-5.688889l56.888889-56.888889c56.888889 11.377778 130.844444 17.066667 210.488888 17.066667 5.688889 34.133333 34.133333 56.888889 68.266667 56.888889s62.577778-22.755556 68.266667-56.888889c130.844444-5.688889 256-17.066667 295.822222-45.511111 28.444444-17.066667 28.444444-34.133333 28.444444-45.511111 11.377778-56.888889-28.444444-102.4-96.711111-136.533334zM216.177778 779.377778c-17.066667-5.688889-34.133333-11.377778-39.822222-11.377778 5.688889-22.755556 39.822222-51.2 91.022222-73.955556 5.688889-5.688889 17.066667-17.066667 17.066666-28.444444V426.666667C284.444444 324.266667 358.4 227.555556 460.8 210.488889c11.377778 0 22.755556-11.377778 22.755556-22.755556 5.688889-17.066667 17.066667-22.755556 28.444444-22.755555s28.444444 5.688889 28.444444 22.755555c0 11.377778 11.377778 17.066667 22.755556 22.755556 56.888889 11.377778 102.4 45.511111 130.844444 85.333333l-477.866666 483.555556z m295.822222 22.755555c-96.711111 0-170.666667-5.688889-227.555556-11.377777l438.044445-443.733334c11.377778 28.444444 17.066667 51.2 17.066667 79.644445v233.244444c0 11.377778 5.688889 22.755556 17.066666 28.444445 51.2 22.755556 85.333333 45.511111 91.022222 73.955555-22.755556 17.066667-142.222222 39.822222-335.644444 39.822222z"
        fill={fill}
      />
    </Svg>
  );
});

const AlertIcon: FC<SvgIconProps> = memo(props => {
  const { fill = '#45484C', ...svgProps } = useSvgProps(props, 24, 24);
  return (
    <Svg viewBox="0 0 1024 1024" {...svgProps}>
      <Path
        d="M505.5 215.9m-62.6 0a62.6 62.6 0 1 0 125.2 0 62.6 62.6 0 1 0-125.2 0Z"
        fill="#FFB89A"
      />
      <Path
        d="M793.4 660.2c-16.2-32.1-22.4-62.8-24.7-78.3-0.6-4.1-0.9-8.3-1-12.5l-1.2-124.5v-3.7c0-2.9-0.1-5.9-0.2-8.8l-0.1-14.2h-0.8c-2.1-22.3-7.2-44.2-15.2-65.2-10.4-27.2-25.5-52.5-44.9-75.2-33.6-39.3-79-69-129.6-85.1-7.4-53.6-49.5-94.7-100.2-94.7-52.7 0-96.1 44.5-100.9 101.1-46.6 18.2-86.9 47.6-117.4 85.9-31.3 39.3-49.8 84.9-54.3 133.2h-0.9v157.4c-0.1 1.6-1.9 27.4-37.6 79.1-13.7 19.8-46.8 70.1-54.6 81.9-10.8 12-14.1 28.8-8.2 44.2 6.4 17 22.3 28 40.5 28h666.8c18.1 0 34-10.9 40.5-27.9 5.9-15.4 2.7-32.2-8.1-44.2-16-24.2-41.8-64.5-47.9-76.5z m-619.8 88.7c13.1-19.8 31.2-47.2 40.2-60.1 16.7-24.2 29.3-47 37.6-67.8 6.7-16.9 10.4-32.2 10.6-44.4v-0.5-2.4V570.8v-0.3l0.3-29.9h-0.4v-62.4h1.1l-1-31c-0.1-2.3-0.1-4.3-0.1-6.1 0-43 14.6-84.1 42.3-118.8 27.5-34.5 65.3-59.9 109.4-73.3l23.9-7.3-2.9-24.8c-0.3-2.5-0.4-5-0.4-7.3 0-28.4 18.5-51.6 41.3-51.6s41.3 23.1 41.3 51.6c0 1.2-0.1 2.5-0.2 4.2l-1.7 25.4 24.8 5.8c47.2 11.1 89.8 36.6 120 71.9 30.6 35.8 46.8 78.8 46.8 124.3 0 1.8 0 3.8-0.1 6.1l-1 31h1.4l0.9 91.7c0.1 6.9 0.6 13.9 1.6 20.7 2.8 19.1 10.5 57 30.5 96.6 6.3 12.4 24.4 40.9 37.7 61.6H173.6zM693 230.5c26.6 8.5 55 36 74 71.8 7.7 14.5 13.4 29.4 17 44.2 3.3 13.7 15.6 22.9 29.1 22.9 2.3 0 4.7-0.3 7.1-0.9 16.1-3.9 26-20.1 22.1-36.2-4.8-19.7-12.3-39.3-22.4-58.2-26.5-49.9-67.2-87.6-108.8-100.8-15.8-5-32.7 3.7-37.7 19.5s3.8 32.7 19.6 37.7zM928.2 296.7c-8.2-29.9-22.2-60.7-40.4-89.2-31.3-48.9-73.7-88.1-116.2-107.6-15.1-6.9-32.9-0.3-39.8 14.8-6.9 15.1-0.3 32.9 14.8 39.8 32.3 14.8 65.4 45.9 90.7 85.4 15 23.4 26.4 48.5 33.1 72.7 3.7 13.3 15.7 22.1 28.9 22.1 2.6 0 5.3-0.3 8-1.1 15.9-4.4 25.3-21 20.9-36.9z"
        fill={fill}
      />
      <Path
        d="M555.8 812.1c-16.6 0-30 13.4-30 30 0 8.5-1.3 16.8-3.8 24.6-5.1 15.8 3.5 32.7 19.2 37.8 3.1 1 6.2 1.5 9.3 1.5 12.6 0 24.4-8 28.5-20.7 4.5-13.8 6.8-28.4 6.8-43.2 0-16.5-13.5-30-30-30zM475.5 906.6c-12.5 0-24.5-6.1-33.9-17.1-10.6-12.4-16.4-29.3-16.4-47.4 0-16.6-13.4-30-30-30s-30 13.4-30 30c0 32.4 10.9 63 30.6 86.2 21 24.7 49.2 38.3 79.6 38.3 16.6 0 30-13.4 30-30s-13.3-30-29.9-30z"
        fill="#33CC99"
      />
    </Svg>
  );
});
